import { useState, useEffect, useCallback } from 'react'
import { Navigate } from 'react-router-dom'
import { Users, Trash2, Plus, Sparkles } from 'lucide-react'
import { useLanguage } from '../contexts/LanguageContext'
import { supabase } from '../lib/supabase'
import { hasTrainerAccess } from '../lib/roles'
import { apiService } from '../services/api'
import type { Group, GroupMember, GroupSession } from '../types/index'

export default function GroupsPage() {
  const { t } = useLanguage()
  const [authUser, setAuthUser] = useState<any>(null)
  const [loading, setLoading] = useState(true)

  const [groups, setGroups] = useState<Group[]>([])
  const [activeGroupId, setActiveGroupId] = useState<string | null>(null)
  const [members, setMembers] = useState<GroupMember[]>([])
  const [latestSession, setLatestSession] = useState<GroupSession | null>(null)

  const [newGroupName, setNewGroupName] = useState('')
  const [newMemberId, setNewMemberId] = useState('')
  const [generating, setGenerating] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    supabase.auth.getUser()
      .then(({ data: { user } }) => setAuthUser(user))
      .catch(() => setAuthUser(null))
      .finally(() => setLoading(false))
  }, [])

  const refreshGroups = useCallback(async () => {
    const res = await apiService.listGroups()
    if (res.success && res.data) setGroups(res.data.groups || [])
  }, [])

  const refreshMembers = useCallback(async (groupId: string) => {
    const res = await apiService.listGroupMembers(groupId)
    if (res.success && res.data) setMembers(res.data.members || [])
  }, [])

  useEffect(() => {
    if (authUser && hasTrainerAccess(authUser)) refreshGroups()
  }, [authUser, refreshGroups])

  useEffect(() => {
    if (activeGroupId) {
      refreshMembers(activeGroupId)
      setLatestSession(null)
    }
  }, [activeGroupId, refreshMembers])

  const handleCreateGroup = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    if (!newGroupName.trim()) return
    const res = await apiService.createGroup({ name: newGroupName.trim() })
    if (res.success && res.data) {
      setNewGroupName('')
      await refreshGroups()
      setActiveGroupId(res.data.group.id)
    } else {
      setError(t('groups.error.generic'))
    }
  }

  const handleAddMember = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    if (!activeGroupId || !newMemberId.trim()) return
    const res = await apiService.addGroupMember(activeGroupId, newMemberId.trim())
    if (res.success) {
      setNewMemberId('')
      await refreshMembers(activeGroupId)
    } else {
      setError(t('groups.error.generic'))
    }
  }

  const handleRemoveMember = async (userId: string) => {
    if (!activeGroupId) return
    const res = await apiService.removeGroupMember(activeGroupId, userId)
    if (res.success) await refreshMembers(activeGroupId)
  }

  const handleGenerate = async () => {
    if (!activeGroupId) return
    setError(null)
    setGenerating(true)
    try {
      const res = await apiService.generateGroupSession(activeGroupId, { duration: 60 })
      if (res.success && res.data) {
        setLatestSession(res.data.session)
      } else {
        setError(res.error === 'invalid' ? t('groups.error.needTwo') : t('groups.error.generic'))
      }
    } finally {
      setGenerating(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-night-950">
        <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  // Coach/trainer-only feature.
  if (!authUser || !hasTrainerAccess(authUser)) {
    return <Navigate to="/dashboard" replace />
  }

  return (
    <main className="min-h-screen bg-gray-50 dark:bg-night-950 px-4 sm:px-6 lg:px-8 py-8">
      <div className="max-w-5xl mx-auto">
        <header className="mb-8">
          <h1 className="flex items-center gap-2 text-2xl font-bold text-gray-900 dark:text-white">
            <Users aria-hidden="true" className="w-6 h-6 text-purple-500" />
            {t('groups.title')}
          </h1>
          <p className="mt-1 text-gray-600 dark:text-slate-300">{t('groups.subtitle')}</p>
        </header>

        {error && (
          <div role="alert" aria-live="assertive" className="mb-6 rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-red-800 dark:border-red-800 dark:bg-red-950/40 dark:text-red-200">
            {error}
          </div>
        )}

        <div className="grid gap-8 md:grid-cols-2">
          {/* Groups column */}
          <section aria-labelledby="groups-heading">
            <h2 id="groups-heading" className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
              {t('groups.create.title')}
            </h2>
            <form onSubmit={handleCreateGroup} className="flex gap-2 mb-6">
              <label htmlFor="new-group-name" className="sr-only">{t('groups.create.namePlaceholder')}</label>
              <input
                id="new-group-name"
                type="text"
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
                placeholder={t('groups.create.namePlaceholder')}
                className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 dark:border-night-700 dark:bg-night-900 dark:text-white"
              />
              <button
                type="submit"
                className="inline-flex items-center gap-1 rounded-lg bg-purple-600 px-4 py-2 font-medium text-white hover:bg-purple-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-2"
              >
                <Plus aria-hidden="true" className="w-4 h-4" />
                {t('groups.create.submit')}
              </button>
            </form>

            {groups.length === 0 ? (
              <p className="text-gray-500 dark:text-slate-400">{t('groups.empty')}</p>
            ) : (
              <ul className="space-y-2">
                {groups.map((g) => (
                  <li key={g.id}>
                    <button
                      type="button"
                      aria-pressed={activeGroupId === g.id}
                      onClick={() => setActiveGroupId(g.id)}
                      className={`w-full rounded-lg border px-4 py-3 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 ${
                        activeGroupId === g.id
                          ? 'border-purple-500 bg-purple-50 dark:bg-purple-950/30'
                          : 'border-gray-200 bg-white hover:border-purple-300 dark:border-night-800 dark:bg-night-900'
                      }`}
                    >
                      <span className="font-medium text-gray-900 dark:text-white">{g.name}</span>
                      <span className="ml-2 text-sm text-gray-500 dark:text-slate-400">({g.group_type})</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* Members + session column */}
          <section aria-labelledby="members-heading">
            <h2 id="members-heading" className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
              {t('groups.members.title')}
            </h2>

            {!activeGroupId ? (
              <p className="text-gray-500 dark:text-slate-400">{t('groups.empty')}</p>
            ) : (
              <>
                <form onSubmit={handleAddMember} className="flex gap-2 mb-4">
                  <label htmlFor="new-member-id" className="sr-only">{t('groups.members.addPlaceholder')}</label>
                  <input
                    id="new-member-id"
                    type="text"
                    value={newMemberId}
                    onChange={(e) => setNewMemberId(e.target.value)}
                    placeholder={t('groups.members.addPlaceholder')}
                    className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 dark:border-night-700 dark:bg-night-900 dark:text-white"
                  />
                  <button
                    type="submit"
                    className="rounded-lg bg-gray-800 px-4 py-2 font-medium text-white hover:bg-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-2 dark:bg-night-700"
                  >
                    {t('groups.members.add')}
                  </button>
                </form>

                {members.length === 0 ? (
                  <p className="text-gray-500 dark:text-slate-400 mb-4">{t('groups.members.empty')}</p>
                ) : (
                  <ul className="space-y-2 mb-4">
                    {members.map((m) => (
                      <li key={m.user_id} className="flex items-center justify-between rounded-lg border border-gray-200 bg-white px-3 py-2 dark:border-night-800 dark:bg-night-900">
                        <span className="text-gray-900 dark:text-white">{m.user_id}</span>
                        <button
                          type="button"
                          onClick={() => handleRemoveMember(m.user_id)}
                          className="inline-flex items-center gap-1 rounded px-2 py-1 text-sm text-red-700 hover:bg-red-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 dark:text-red-300 dark:hover:bg-red-950/30"
                          aria-label={`${t('groups.members.remove')} ${m.user_id}`}
                        >
                          <Trash2 aria-hidden="true" className="w-4 h-4" />
                          {t('groups.members.remove')}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}

                <button
                  type="button"
                  onClick={handleGenerate}
                  disabled={generating || members.length < 2}
                  className="inline-flex items-center gap-2 rounded-lg bg-purple-600 px-4 py-2 font-medium text-white hover:bg-purple-700 disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-2"
                >
                  <Sparkles aria-hidden="true" className="w-4 h-4" />
                  {generating ? t('groups.generating') : t('groups.generate')}
                </button>

                <div aria-live="polite">
                  {latestSession && (
                    <article className="mt-6 rounded-lg border border-gray-200 bg-white p-4 dark:border-night-800 dark:bg-night-900">
                      <h3 className="font-semibold text-gray-900 dark:text-white mb-2">{t('groups.session.title')}</h3>
                      <pre className="overflow-x-auto whitespace-pre-wrap text-sm text-gray-700 dark:text-slate-300">
                        {JSON.stringify(latestSession.optimizer_output?.session_overview ?? latestSession.optimizer_output, null, 2)}
                      </pre>
                    </article>
                  )}
                </div>
              </>
            )}
          </section>
        </div>
      </div>
    </main>
  )
}
